import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  listRequirements,
  type Requirement,
} from '../api/requirements';
import { AppShell } from '../components/AppShell';
import { Hero } from '../components/Hero';
import { FilterBar, type ClassFilter, type InboxTab } from '../components/FilterBar';
import { RequirementRow } from '../components/RequirementRow';
import { DetailDrawer } from '../components/DetailDrawer';
import { Toast } from '../components/Toast';

const TOAST_DURATION_MS = 2400;

export function Inbox() {
  const navigate = useNavigate();
  const { id: drawerId } = useParams<{ id: string }>();

  const { data, isLoading, error } = useQuery({
    queryKey: ['requirements'],
    queryFn: listRequirements,
    refetchInterval: (q) => {
      const list = q.state.data as Requirement[] | undefined;
      const hasRefining = list?.some((r) => r.status === 'REFINING' && !r.lastError);
      return hasRefining ? 3000 : false;
    },
  });

  const [tab, setTab] = useState<InboxTab>('open');
  const [classFilter, setClassFilter] = useState<ClassFilter>('all');
  const [customerFilter, setCustomerFilter] = useState<string>('all');
  const [toast, setToast] = useState<string | null>(null);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), TOAST_DURATION_MS);
    return () => clearTimeout(t);
  }, [toast]);

  const items = useMemo<readonly Requirement[]>(() => data ?? [], [data]);

  const stats = useMemo(
    () => ({
      open: items.filter((r) => r.status !== 'RELEASED' && r.status !== 'ARCHIVED').length,
      gate1: items.filter((r) => r.status === 'TRIAGE').length,
      gate2: items.filter((r) => r.status === 'READY_FOR_RELEASE').length,
      clarification: items.filter((r) => r.status === 'CLARIFICATION').length,
    }),
    [items],
  );

  const tabCounts = useMemo(
    () => ({
      open: stats.open,
      clarification: stats.clarification,
      released: items.filter((r) => r.status === 'RELEASED').length,
    }),
    [items, stats],
  );

  const customers = useMemo(() => {
    const set = new Set<string>();
    for (const r of items) {
      const c = r.customer?.trim();
      if (c) set.add(c);
    }
    return Array.from(set).sort((a, b) => a.localeCompare(b, 'de'));
  }, [items]);

  const filtered = useMemo(() => {
    return items.filter((r) => {
      if (tab === 'open' && (r.status === 'RELEASED' || r.status === 'ARCHIVED')) return false;
      if (tab === 'clarification' && r.status !== 'CLARIFICATION') return false;
      if (tab === 'released' && r.status !== 'RELEASED') return false;
      if (classFilter !== 'all' && r.classification !== classFilter) return false;
      if (customerFilter !== 'all' && (r.customer ?? '') !== customerFilter) return false;
      return true;
    });
  }, [items, tab, classFilter, customerFilter]);

  const topbarAction = (
    <button
      type="button"
      className="btn-primary-app"
      onClick={() => navigate('/submit')}
    >
      Neue Anforderung
    </button>
  );

  return (
    <AppShell crumbs={['Mindwright', 'Inbox']} inboxCount={stats.open} topbarAction={topbarAction}>
      <div className="page">
        <Hero stats={stats} />

        <FilterBar
          tab={tab}
          tabCounts={tabCounts}
          onTabChange={setTab}
          classFilter={classFilter}
          onClassFilterChange={setClassFilter}
          customerFilter={customerFilter}
          customers={customers}
          onCustomerFilterChange={setCustomerFilter}
        />

        <div className="table-wrap">
          <div className="thead">
            <div>Anforderung</div>
            <div>Typ</div>
            <div>Kunde</div>
            <div>Workflow</div>
            <div>Eingang</div>
            <div />
          </div>
          {isLoading && (
            <div className="loading-row" role="status">
              Lade Anforderungen …
            </div>
          )}
          {error && (
            <div className="error-row" role="alert">
              Fehler beim Laden der Anforderungen.
            </div>
          )}
          {!isLoading && !error && items.length === 0 && (
            <div className="empty-row">Keine Anforderungen vorhanden.</div>
          )}
          {!isLoading && !error && items.length > 0 && filtered.length === 0 && (
            <div className="empty-row">Keine Einträge für diese Filter.</div>
          )}
          {filtered.map((r) => (
            <RequirementRow key={r.id} requirement={r} />
          ))}
        </div>
      </div>

      {drawerId && (
        <DetailDrawer
          key={drawerId}
          requirementId={drawerId}
          onClose={() => navigate('/inbox')}
          onMutated={(msg) => setToast(msg)}
        />
      )}

      <Toast message={toast} />
    </AppShell>
  );
}
